import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    
    if (response.data.requires_mfa) {
      setMfaPending({
        email: response.data.email,
        mfa_type: response.data.mfa_type
      });
      return { requiresMfa: true, mfaType: response.data.mfa_type };
    }
    
    const { access_token, user: userData, must_change_password } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return { requiresMfa: false, mustChangePassword: must_change_password || false };
  };

  const verifyMfa = async (email, code) => {
    const response = await axios.post(`${API}/auth/verify-mfa`, { email, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setMfaPending(null);
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      first_name: firstName,
      last_name: lastName
    });
    return response.data;
  };

  const setupMfa = async (mfaType) => {
    const response = await axios.post(`${API}/auth/setup-mfa`, 
      { mfa_type: mfaType },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    // Refresh user data
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(userResponse.data);
    return response.data;
  };

  const disableMfa = async () => {
    await axios.post(`${API}/auth/disable-mfa`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(userResponse.data);
  };

  const sendEmailOtp = async (email) => {
    const response = await axios.post(`${API}/auth/send-email-otp`, { email });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setMfaPending(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      mfaPending,
      login,
      verifyMfa,
      register,
      setupMfa,
      disableMfa,
      sendEmailOtp,
      logout,
      isAuthenticated: !!token && !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
